import * as XLSX from 'xlsx';
import { getConfig } from 'utils/appConfig';

// ============================================================
// reporteExport.ts — Utilidades de exportación para reportes
// ============================================================

/** Exporta a Excel con una o varias hojas, incluyendo cabecera con nombre de institución. */
export const exportarExcel = (
  nombreArchivo: string,
  hojas: { nombre: string; filas: (string | number | null)[][] }[],
) => {
  const wb = XLSX.utils.book_new();
  const institucion = getConfig().institucionNombre || 'Institución Educativa';
  const fecha = new Date().toLocaleDateString('es-PE', {
    day: '2-digit', month: 'long', year: 'numeric',
  });

  hojas.forEach(h => {
    // Cabecera institucional (4 filas antes de los datos)
    const cabecera: (string | number | null)[][] = [
      [institucion],
      ['SAE — Sistema de Administración Escolar'],
      [`Generado el: ${fecha}`],
      [],
    ];
    const filasCompletas = [...cabecera, ...h.filas];
    const ws = XLSX.utils.aoa_to_sheet(filasCompletas);

    // Determinar número de columnas para el merge
    const numCols = Math.max(...h.filas.map(r => r.length), 1);
    if (numCols > 1) {
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: numCols - 1 } },
      ];
    }

    // Autofit columns (approximate)
    const colWidths = filasCompletas.reduce<number[]>((acc, row) => {
      row.forEach((cell, i) => {
        const len = cell === null ? 4 : String(cell).length;
        acc[i] = Math.max(acc[i] ?? 6, len + 2);
      });
      return acc;
    }, []);
    ws['!cols'] = colWidths.map(w => ({ wch: Math.min(w, 50) }));
    XLSX.utils.book_append_sheet(wb, ws, h.nombre.slice(0, 31));
  });
  XLSX.writeFile(wb, `${nombreArchivo}.xlsx`);
};

/**
 * Abre una nueva ventana mostrando múltiples boletas, cada una en su propia
 * "hoja" A4 visible en pantalla. Al imprimir, cada boleta inicia en página nueva.
 *
 * Diferencias con exportarPDF:
 *  - body con fondo gris → cada boleta es una tarjeta blanca tipo hoja física
 *  - break-before:page   → salto de página real entre boletas al imprimir
 *  - Sin encabezado SAE global → cada htmlBoleta ya trae su propio encabezado SIAGIE
 */
export const exportarPDFMasivo = (titulo: string, paginas: string[]) => {
  const w = window.open('', '_blank', 'width=1060,height=800');
  if (!w) { alert('Permite ventanas emergentes para exportar el PDF.'); return; }

  const fecha = new Date().toLocaleDateString('es-PE', {
    day: '2-digit', month: 'long', year: 'numeric',
  });

  const contenido = paginas.map((html, i) =>
    `<div class="boleta-hoja${i > 0 ? ' salto' : ''}">${html}</div>`,
  ).join('');

  w.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>${titulo}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:Arial,Helvetica,sans-serif;font-size:11px;color:#1e293b;
         background:#94a3b8;padding:24px;}
    .barra-impresion{
      background:#1e3a8a;color:#fff;border-radius:8px;
      padding:10px 20px;margin-bottom:20px;
      display:flex;align-items:center;justify-content:space-between;
      font-size:12px;
    }
    .barra-impresion button{
      background:#fff;color:#1e3a8a;border:none;
      padding:7px 22px;border-radius:6px;font-size:12px;
      font-weight:700;cursor:pointer;
    }
    .boleta-hoja{
      background:white;
      max-width:210mm;
      min-height:285mm;
      margin:0 auto 24px;
      padding:12mm;
      border-radius:4px;
      box-shadow:0 4px 16px rgba(0,0,0,.25);
    }
    table{width:100%;border-collapse:collapse;margin-top:8px;}
    thead tr{background:#1e3a8a;}
    th{text-align:left;padding:5px 7px;font-size:9px;color:#fff;
       text-transform:uppercase;letter-spacing:.05em;font-weight:700;}
    td{padding:4px 7px;border-bottom:1px solid #f1f5f9;vertical-align:top;}
    tr:nth-child(even) td{background:#f8fafc;}
    @media print{
      @page{size:A4 portrait;margin:12mm;}
      body{background:white;padding:0;}
      .barra-impresion{display:none!important;}
      .boleta-hoja{
        box-shadow:none;border-radius:0;padding:0;
        max-width:none;min-height:auto;margin:0;
      }
      .salto{break-before:page;page-break-before:always;}
    }
  </style>
</head>
<body>
  <div class="barra-impresion">
    <span>${titulo} &nbsp;·&nbsp; Generado el ${fecha}</span>
    <button onclick="window.print()">🖨 Imprimir / Guardar PDF</button>
  </div>
  ${contenido}
</body>
</html>`);
  w.document.close();
};

/** Abre una nueva ventana con el contenido HTML y llama a window.print() para guardar como PDF.
 *  @param orientacion  'portrait' para boletas individuales, 'landscape' para actas/tablas anchas (default). */
export const exportarPDF = (
  titulo: string,
  contenido: string,
  orientacion: 'portrait' | 'landscape' = 'landscape',
) => {
  const w = window.open('', '_blank', 'width=1000,height=700');
  if (!w) { alert('Permite ventanas emergentes para exportar el PDF.'); return; }

  const institucion = getConfig().institucionNombre || 'Institución Educativa';
  const fecha = new Date().toLocaleDateString('es-PE', {
    day: '2-digit', month: 'long', year: 'numeric',
  });

  w.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>${titulo}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:Arial,Helvetica,sans-serif;font-size:11px;color:#1e293b;padding:20px;}
    .inst{font-size:13px;font-weight:700;color:#1e3a8a;margin-bottom:1px;}
    h1{font-size:15px;color:#1e293b;margin-bottom:2px;}
    .sub{font-size:10px;color:#64748b;margin-bottom:14px;}
    .kpi-row{display:flex;gap:10px;margin-bottom:14px;flex-wrap:wrap;}
    .kpi{border:1px solid #e2e8f0;border-radius:6px;padding:8px 12px;min-width:140px;}
    .kpi-lbl{font-size:9px;color:#64748b;text-transform:uppercase;font-weight:700;letter-spacing:.05em;}
    .kpi-val{font-size:16px;font-weight:700;color:#1e293b;margin-top:1px;}
    .section-hdr{background:#f1f5f9;padding:5px 8px;font-weight:700;font-size:10px;
                  margin-top:10px;border-radius:4px;color:#475569;}
    table{width:100%;border-collapse:collapse;margin-top:8px;}
    thead tr{background:#1e3a8a;}
    th{text-align:left;padding:5px 7px;font-size:9px;color:#fff;
       text-transform:uppercase;letter-spacing:.05em;font-weight:700;}
    td{padding:4px 7px;border-bottom:1px solid #f1f5f9;vertical-align:top;}
    tr:nth-child(even) td{background:#f8fafc;}
    .badge{display:inline-block;padding:1px 6px;border-radius:99px;font-size:9px;font-weight:700;}
    .no-print{margin-top:18px;text-align:center;}
    .no-print button{background:#1e3a8a;color:#fff;border:none;padding:7px 22px;
                      border-radius:6px;font-size:12px;cursor:pointer;}
    @media print{
      @page{size:A4 ${orientacion};margin:12mm;}
      .no-print{display:none!important;}
      body{padding:0;}
    }
  </style>
</head>
<body>
  <p class="inst">${institucion}</p>
  <h1>${titulo}</h1>
  <p class="sub">SAE — Sistema de Administración Escolar &nbsp;·&nbsp; Generado el ${fecha}</p>
  ${contenido}
  <div class="no-print">
    <button onclick="window.print()">Imprimir / Guardar como PDF</button>
  </div>
</body>
</html>`);
  w.document.close();
};
